import { FileIcon } from '@phosphor-icons/react';
import type { ElementType } from 'react';
import type { TreeNodeResponse } from './cors-provenance-api';
import { fileTypeControllers } from './constants';
import { fileTypeIcons } from './uiConstants';

/** Last segment of a searchable_path-style tree path. */
export function pathTail(path: string): string {
  const parts = path.split('/').filter(Boolean);
  return parts.length ? parts[parts.length - 1]! : '';
}

/**
 * When a case tag filters the tree, the API often returns a single top-level
 * directory for that case. Treat it as the browse root: list its children
 * without showing that folder as the first row.
 *
 * Includes directories even when ``hasChildren`` is false so empty/new cases
 * still resolve a browse root.
 */
export function pickCaseRootDirectoryNode(
  items: TreeNodeResponse[],
  caseTag: string,
  matchId?: string | null,
): TreeNodeResponse | undefined {
  const directories = items.filter((n) => n.nodeType === 'directory');
  if (directories.length === 0) return undefined;
  const tagTrim = caseTag.trim();
  const matchTrim = matchId?.trim();
  if (matchTrim) {
    const byId = directories.find(
      (n) =>
        n.name === matchTrim ||
        n.path === matchTrim ||
        pathTail(n.path) === matchTrim ||
        n.path.endsWith(`/${matchTrim}`),
    );
    if (byId) return byId;
  }
  const byTag = directories.find(
    (n) =>
      n.path === tagTrim ||
      pathTail(n.path) === tagTrim ||
      n.name === tagTrim ||
      n.path.endsWith(`/${tagTrim}`),
  );
  if (byTag) return byTag;
  if (directories.length === 1) return directories[0];
  return undefined;
}

/** Normalized file/subdocument metadata for tree row rendering. */
export function treeNodeMeta(node: TreeNodeResponse): {
  size: number | undefined;
  status: string | undefined;
  mimeType: string | undefined;
  contentCategories: string[] | undefined;
  extension: string | undefined;
  pageCount: number | undefined;
  subdocumentCount: number | undefined;
  fileUrl: string | undefined;
} {
  const fi = node.fileInfo;
  if (fi?.kind === 'file') {
    const f = fi.file;
    return {
      size: f.size,
      status: f.status,
      mimeType: f.mimeType,
      contentCategories: f.contentCategories,
      extension: f.extension,
      pageCount: f.pageCount ?? undefined,
      subdocumentCount: f.subdocumentCount,
      fileUrl: f.fileUrl,
    };
  }
  if (fi?.kind === 'subdocument') {
    const s = fi.subdocument;
    return {
      size: s.size,
      status: s.status,
      mimeType: s.mimeType,
      contentCategories: s.contentCategories,
      extension: s.extension,
      pageCount: undefined,
      subdocumentCount: undefined,
      fileUrl: s.fileUrl,
    };
  }
  return {
    size: undefined,
    status: undefined,
    mimeType: undefined,
    contentCategories: undefined,
    extension: undefined,
    pageCount: undefined,
    subdocumentCount: undefined,
    fileUrl: undefined,
  };
}

const EXTENSION_TO_FILETYPE_KEY: Record<string, keyof typeof fileTypeIcons> = (() => {
  const map: Record<string, keyof typeof fileTypeIcons> = {};
  for (const [label, exts] of Object.entries(fileTypeControllers)) {
    for (const ext of exts) {
      map[ext.toLowerCase()] = label as keyof typeof fileTypeIcons;
    }
  }
  return map;
})();

/** File-type icon from a path segment name (extension only; no file metadata). */
export function iconForFileName(name: string): ElementType {
  return iconForTreeNode({
    name,
    path: name,
    nodeType: 'file',
    hasChildren: false,
  });
}

/** Pick a row icon for a provenance tree node (directories use the caller's folder icon). */
export function iconForTreeNode(node: TreeNodeResponse): ElementType {
  const { extension, mimeType } = treeNodeMeta(node);
  const extKey = (extension ?? '').toLowerCase().replace(/^\./, '');
  if (extKey && EXTENSION_TO_FILETYPE_KEY[extKey]) {
    return fileTypeIcons[EXTENSION_TO_FILETYPE_KEY[extKey]];
  }
  const nameExt = node.name.split('.').pop()?.toLowerCase() ?? '';
  if (nameExt && EXTENSION_TO_FILETYPE_KEY[nameExt]) {
    return fileTypeIcons[EXTENSION_TO_FILETYPE_KEY[nameExt]];
  }
  if (mimeType?.startsWith('image/')) return fileTypeIcons.Image;
  if (mimeType?.startsWith('video/')) return fileTypeIcons.Video;
  if (mimeType?.startsWith('audio/')) return fileTypeIcons.Audio;
  if (mimeType === 'application/pdf') return fileTypeIcons.PDF;
  if (mimeType?.startsWith('message/') || mimeType === 'application/mbox') {
    return fileTypeIcons.Email;
  }
  return FileIcon;
}
